import { z } from 'zod';
import {
  AckSchema,
  CursorInputSchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateTimeSchema,
  pageOf,
  withMeta,
} from '../common';
import { CalendarSchema, NewsSchema, RiskControlMetaSchema, RuleMetaSchema } from '../dataset/core';
import { EodBypassSessionSchema } from '../dataset/eod';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** prepare.* — AI Briefing, Session Setup, Risk Rules, Risk Profile, Strategies (SDD §7.4 Prepare rows; §11.2 session machine). */

// ── News brief + watchlist ───────────────────────────────────────────────
export const SentimentChipSchema = z
  .object({
    symbol: z.string(),
    score: z.number().min(-1).max(1),
    label: z.enum(['bearish', 'neutral', 'bullish']),
  })
  .strict();
export const NewsBriefSchema = withMeta({
  briefId: IdSchema,
  state: z.enum(['ready', 'generating']),
  generatedAt: IsoDateTimeSchema,
  headline: z.string(),
  news: z.array(NewsSchema),
  calendar: z.array(CalendarSchema),
  sentiment: z.array(SentimentChipSchema),
  /** Refresh is rate-limited per plan; null when a refresh is allowed now. */
  refreshAllowedAt: IsoDateTimeSchema.nullable(),
});
export type NewsBrief = z.infer<typeof NewsBriefSchema>;
export const WatchlistSchema = withMeta({
  items: z.array(z.object({ symbol: z.string(), addedAt: IsoDateTimeSchema }).strict()),
});

// ── Session machine (§11.2): none → preparing → armed → closing → closed; bypassed is audited ──
export const SessionStateSchema = z.enum(['none', 'preparing', 'armed', 'closing', 'closed', 'bypassed']);
export type SessionState = z.infer<typeof SessionStateSchema>;
export const SessionCapsSchema = z
  .object({
    maxTrades: z.number().int().min(0).max(50),
    maxLossR: z.number().min(0).max(20),
    maxRiskPerTradePct: z.number().min(0).max(10),
  })
  .strict();
export const ChecklistItemSchema = z.object({ id: IdSchema, text: z.string(), done: z.boolean() }).strict();
export const RedFlagSeveritySchema = z.enum(['low', 'medium', 'high']);
export const RedFlagSchema = z
  .object({ key: z.string(), severity: RedFlagSeveritySchema, text: z.string(), evidence: z.string() })
  .strict();
export const SessionOverrideSchema = z
  .object({ at: IsoDateTimeSchema, flagKeys: z.array(z.string()), reason: z.string() })
  .strict();
export const TradingSessionSchema = withMeta({
  sessionId: IdSchema.nullable(),
  state: SessionStateSchema,
  armedAt: IsoDateTimeSchema.nullable(),
  closedAt: IsoDateTimeSchema.nullable(),
  mood: z.string().nullable(),
  energy: z.number().int().min(1).max(10).nullable(),
  caps: SessionCapsSchema,
  checklist: z.array(ChecklistItemSchema),
  strategies: z.array(z.string()),
  redFlags: z.array(RedFlagSchema),
  overrides: z.array(SessionOverrideSchema),
  bypassed: z.boolean(),
  budget: z.object({ tradesUsed: z.number().int(), lossUsedR: z.number() }).strict(),
  reflection: z.string().nullable(),
  /** The goal stated at arming — the read-back of the arm input's dailyGoal. */
  dailyGoal: z.string().nullable(),
  /** Snapshotted versions at arming (rule set / profile / strategies). */
  versions: z.object({ ruleSet: z.number().int(), profile: z.number().int() }).strict().nullable(),
});
export type TradingSession = z.infer<typeof TradingSessionSchema>;
export const ArmSessionInputSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    mood: z.string().min(1).max(32),
    energy: z.number().int().min(1).max(10),
    caps: SessionCapsSchema,
    checklist: z.array(z.object({ id: IdSchema, done: z.boolean() }).strict()),
    strategies: z.array(z.string()).min(1).max(12),
    /** The brief's stated goal for the day (mandate §4) — recorded on the session and titling the day's
     *  journal entry; empty means the trader chose not to state one, never a fabricated default. */
    dailyGoal: z.string().max(140).optional(),
    /** Voice/composer arming requires explicit budget confirmation (§11.2). */
    budgetConfirmed: z.boolean(),
    /** Bypass = arm without preparation; audited, appears in EOD. */
    bypass: z
      .object({ reason: z.string().min(1).max(280) })
      .strict()
      .optional(),
  })
  .strict();
export const AmendSessionInputSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    caps: SessionCapsSchema.partial().optional(),
    strategies: z.array(z.string()).min(1).max(12).optional(),
    checklist: z.array(z.object({ id: IdSchema, done: z.boolean() }).strict()).optional(),
  })
  .strict();
export const RedFlagScanSchema = withMeta({ scannedAt: IsoDateTimeSchema, flags: z.array(RedFlagSchema) });
export const BypassAuditSchema = withMeta({
  sessions: z.array(EodBypassSessionSchema),
  preparedVsBypassed: z
    .object({ preparedExpectancyR: z.number(), bypassedExpectancyR: z.number(), advantageR: z.number() })
    .strict(),
});

// ── Suggestions loop (insights / EOD / intel → next arming) ──
export const SuggestionSchema = z
  .object({
    id: IdSchema,
    source: z.enum(['insight', 'eod', 'intel', 'digest']),
    text: z.string(),
    createdAt: IsoDateTimeSchema,
    status: z.enum(['pending', 'accepted', 'dismissed']),
  })
  .strict();

// ── Risk rules (engine rule set) ──
export const RuleStateSchema = z
  .object({
    key: z.string(),
    enabled: z.boolean(),
    params: z.record(z.string(), z.string()),
    /** Weakening a rule is delayed: the change takes effect after the refractory window. */
    effectiveAt: IsoDateTimeSchema.nullable(),
  })
  .strict();
/** A trader-authored advisory rule (SDD §"one catalogue, three enforcement classes" — this is the advisory
 *  class): it surfaces on the TradeHub monitor when it fires and counts in the engine panel, and it NEVER
 *  gates an order — admission stays with the system catalogue. */
export const CustomRuleSchema = z
  .object({
    id: IdSchema,
    label: z.string(),
    expression: z.string(),
    severity: z.enum(['soft', 'hard']),
    enabled: z.boolean(),
    lastFiredAt: IsoDateTimeSchema.nullable(),
  })
  .strict();
export type CustomRule = z.infer<typeof CustomRuleSchema>;

export const RuleCatalogueSchema = withMeta({
  version: z.number().int(),
  rules: z.array(RuleMetaSchema),
  state: z.array(RuleStateSchema),
  /** Trader-authored advisory rules — rendered after the system cards, empty for a fresh account. */
  custom: z.array(CustomRuleSchema),
  /** Rule-engine status summary the panel shows (full status: engine.status.get). */
  engine: z
    .object({ status: z.enum(['running', 'degraded', 'stopped']), lastTickAt: IsoDateTimeSchema })
    .strict(),
});
export const RuleUpdateInputSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    key: z.string(),
    enabled: z.boolean().optional(),
    params: z.record(z.string(), z.string()).optional(),
  })
  .strict();
export const RuleBreachSchema = z
  .object({
    id: IdSchema,
    ruleKey: z.string(),
    at: IsoDateTimeSchema,
    detail: z.string(),
    action: z.enum(['warned', 'blocked', 'locked', 'closed_positions']),
    sessionId: IdSchema.nullable(),
  })
  .strict();

// ── Risk profile (controls + audit; pending changes with effective-at) ──
export const RiskProfileSchema = withMeta({
  version: z.number().int(),
  controls: z.record(z.string(), RiskControlMetaSchema),
  values: z.record(z.string(), z.string()),
  /** Settings frozen during hard lockout (§7.4). */
  frozen: z.boolean(),
  updatedAt: IsoDateTimeSchema,
});
export const PendingChangeSchema = z
  .object({ id: IdSchema, key: z.string(), from: z.string(), to: z.string(), effectiveAt: IsoDateTimeSchema })
  .strict();
export const ProfileVersionSchema = z
  .object({
    version: z.number().int(),
    at: IsoDateTimeSchema,
    changes: z.array(z.object({ key: z.string(), from: z.string(), to: z.string() }).strict()),
  })
  .strict();
export const PropPresetSchema = z
  .object({ id: IdSchema, firm: z.string(), name: z.string(), values: z.record(z.string(), z.string()) })
  .strict();

// ── Strategies (playbook) ──
export const StrategyStatusSchema = z.enum(['active', 'paused', 'archived']);
export const StrategySchema = z
  .object({
    id: IdSchema,
    name: z.string(),
    status: StrategyStatusSchema,
    version: z.number().int(),
    rules: z.array(z.string()),
    sessions: z.array(z.string()),
    stats: z.object({ trades: z.number().int(), winRate: z.number(), expectancyR: z.number() }).strict(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();
export const StrategyComplianceSchema = withMeta({
  taggedPct: z.number(),
  untagged: z.number().int(),
  onPlanPct: z.number(),
  /** Where untagged trades can be tagged from (TradeHub / Trades / Journal). */
  callToAction: z.array(z.enum(['tradehub', 'trades', 'journal'])),
});

export const prepareProcedures = defineProcedures({
  'prepare.newsBrief.get': query(
    'Ranked pre-session brief (news, calendar, sentiment)',
    z.void(),
    NewsBriefSchema,
  ),
  'prepare.newsBrief.refresh': mutation(
    'Regenerate the brief (rate-limited per plan → RATE_LIMITED / NOT_ENTITLED)',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    NewsBriefSchema,
  ),
  'prepare.watchlist.list': query('Watchlist symbols', z.void(), WatchlistSchema),
  'prepare.watchlist.add': mutation(
    'Add a symbol',
    z.object({ symbol: z.string().min(3).max(12) }).strict(),
    WatchlistSchema,
  ),
  'prepare.watchlist.remove': mutation(
    'Remove a symbol',
    z.object({ symbol: z.string().min(3).max(12) }).strict(),
    WatchlistSchema,
  ),

  'prepare.session.current': query(
    'Current trading session + state machine position',
    z.void(),
    TradingSessionSchema,
  ),
  'prepare.session.arm': mutation(
    'Arm a session (snapshots versions; journal auto-creates)',
    ArmSessionInputSchema,
    TradingSessionSchema,
  ),
  'prepare.session.amend': mutation(
    'Amend caps/strategies/checklist of the armed session',
    AmendSessionInputSchema,
    TradingSessionSchema,
  ),
  'prepare.session.close': mutation(
    'Close the session with a reflection (EOD issues ≤ 10 min after)',
    z.object({ idempotencyKey: IdempotencyKeySchema, reflection: z.string().max(2000).nullable() }).strict(),
    TradingSessionSchema,
  ),
  'prepare.session.redFlagScan': query(
    'Red-flag scan over recent behaviour (severity-tinted)',
    z.void(),
    RedFlagScanSchema,
  ),
  'prepare.session.override': mutation(
    'Override red flags with a reason (audited; renders in EOD)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        flagKeys: z.array(z.string()).min(1),
        reason: z.string().min(1).max(280),
      })
      .strict(),
    TradingSessionSchema,
  ),
  'prepare.session.bypassAudit': query(
    'Preparation bypass audit + prepared-vs-bypassed advantage',
    z.void(),
    BypassAuditSchema,
  ),
  'prepare.suggestions.list': query(
    'Pending suggestions for the next arming',
    z.void(),
    withMeta({ items: z.array(SuggestionSchema) }),
  ),
  'prepare.suggestions.accept': mutation(
    'Accept a suggestion into the session plan',
    z.object({ id: IdSchema }).strict(),
    withMeta({ items: z.array(SuggestionSchema) }),
  ),
  'prepare.suggestions.dismiss': mutation(
    'Dismiss a suggestion',
    z.object({ id: IdSchema }).strict(),
    withMeta({ items: z.array(SuggestionSchema) }),
  ),

  'prepare.riskRules.catalogue': query(
    'Rule catalogue + current rule-set state + engine summary',
    z.void(),
    RuleCatalogueSchema,
  ),
  'prepare.riskRules.update': mutation(
    'Enable/disable/param a rule (weakening is refractory-delayed)',
    RuleUpdateInputSchema,
    RuleCatalogueSchema,
  ),
  'prepare.riskRules.customCreate': mutation(
    'Create an advisory custom rule (never an order gate)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        label: z.string().min(1).max(80),
        expression: z.string().min(1).max(280),
        severity: z.enum(['soft', 'hard']),
      })
      .strict(),
    RuleCatalogueSchema,
  ),
  'prepare.riskRules.customUpdate': mutation(
    'Enable/disable a custom rule',
    z.object({ idempotencyKey: IdempotencyKeySchema, id: IdSchema, enabled: z.boolean() }).strict(),
    RuleCatalogueSchema,
  ),
  'prepare.riskRules.customDelete': mutation(
    'Delete a custom rule (custom rules delete; system rules toggle)',
    z.object({ idempotencyKey: IdempotencyKeySchema, id: IdSchema }).strict(),
    RuleCatalogueSchema,
  ),
  'prepare.riskRules.breachHistory': query(
    'Immutable breach history (cursor)',
    CursorInputSchema.optional(),
    pageOf(RuleBreachSchema),
  ),

  'prepare.riskProfile.get': query(
    'Risk profile controls + values + frozen flag',
    z.void(),
    RiskProfileSchema,
  ),
  'prepare.riskProfile.update': mutation(
    'Change controls; loosening becomes a pending change with an effective-at',
    z.object({ idempotencyKey: IdempotencyKeySchema, changes: z.record(z.string(), z.string()) }).strict(),
    withMeta({ profile: RiskProfileSchema, pending: z.array(PendingChangeSchema) }),
  ),
  'prepare.riskProfile.history': query(
    'Profile version history (audit tab)',
    CursorInputSchema.optional(),
    pageOf(ProfileVersionSchema),
  ),
  'prepare.riskProfile.propPresets': query(
    'Prop-firm presets',
    z.void(),
    withMeta({ presets: z.array(PropPresetSchema) }),
  ),
  'prepare.access.pendingChanges': query(
    'Pending risk-setting changes with effective-at countdown',
    z.void(),
    withMeta({ items: z.array(PendingChangeSchema) }),
  ),
  'prepare.access.cancel': mutation(
    'Cancel a pending risk-setting change',
    z.object({ id: IdSchema }).strict(),
    withMeta({ items: z.array(PendingChangeSchema) }),
  ),

  'prepare.strategies.list': query(
    'Playbook strategies with stats',
    z.void(),
    withMeta({ items: z.array(StrategySchema) }),
  ),
  'prepare.strategies.create': mutation(
    'Create a strategy',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        name: z.string().min(1).max(64),
        rules: z.array(z.string()).max(20),
        sessions: z.array(z.string()).max(8),
      })
      .strict(),
    StrategySchema,
  ),
  'prepare.strategies.update': mutation(
    'Update a strategy (versioned)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        id: IdSchema,
        name: z.string().min(1).max(64).optional(),
        status: z.enum(['active', 'paused']).optional(),
        rules: z.array(z.string()).max(20).optional(),
        sessions: z.array(z.string()).max(8).optional(),
      })
      .strict(),
    StrategySchema,
  ),
  'prepare.strategies.archive': mutation(
    'Archive a strategy',
    z.object({ idempotencyKey: IdempotencyKeySchema, id: IdSchema }).strict(),
    StrategySchema,
  ),
  'prepare.strategies.linkTrade': mutation(
    'Tag a trade with a strategy (feeds compliance)',
    z
      .object({ idempotencyKey: IdempotencyKeySchema, tradeId: z.string(), strategyId: IdSchema.nullable() })
      .strict(),
    AckSchema,
  ),
  'prepare.strategies.compliance': query(
    'Compliance meter + untagged-trades call to action',
    z.void(),
    StrategyComplianceSchema,
  ),
});
export type PrepareFamilyClient = RouterShape<typeof prepareProcedures>;

export const prepareFamily = defineFamily({
  name: 'prepare',
  title: 'Prepare',
  screens: ['AI Briefing', 'Prepare (Session Setup)', 'Risk Rules', 'Risk Profile', 'Strategies'],
  procedures: prepareProcedures,
  binding: 'simulated',
});
